import React from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Select, { components as selectComponents } from 'react-select';
import AsyncSelect from 'react-select/async';
import { fetchData } from '../utils/apiHelpers';
import { getAll } from '../api';
import { getCache, getOrFetch, subscribeToLookupCache } from './lookupCache';
import { formatDepartmentOption } from './formatDepartmentOption';
import { formatLocal } from '../shared/dates';
import { getUserTimeZone } from '../shared/timezone';
import { HierarchySelect } from '../components/common/HierarchySelect';
import { enhanceHierarchyOptions } from './hierarchy';
import { sortLookupOptions } from './sortLookupOptions';
import {
  HIERARCHY_STRUCTURE_API,
  resolveHierarchyApiName,
} from '../constants/hierarchy';

const sanitizeCodePart = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const getNestedValue = (record, path) => {
  if (!record || typeof record !== 'object' || typeof path !== 'string' || !path.trim()) {
    return undefined;
  }

  return path.split('.').reduce((acc, key) => {
    if (acc === undefined || acc === null) return undefined;
    return acc[key];
  }, record);
};

const buildCodeNameLabel = (record) => {
  if (!record || typeof record !== 'object') return '';

  const code = sanitizeCodePart(record.code);
  const name = sanitizeCodePart(record.name);
  const parts = [code, name].filter(Boolean);
  return parts.join(' - ');
};

const getOptionLabel = (record, api, displayField) => {
  const codeNameLabel = api === 'task-codes' ? buildCodeNameLabel(record) : '';
  if (codeNameLabel) {
    return codeNameLabel;
  }

  if (typeof displayField === 'function') {
    return displayField(record);
  }

  if (displayField && record && record[displayField] !== undefined && record[displayField] !== null) {
    return record[displayField];
  }

  if (record && typeof record.label === 'string') {
    return record.label;
  }

  return '';
};

const parseTaskCodeSortParts = (value) => {
  const trimmed = sanitizeCodePart(value);
  if (!trimmed) {
    return { number: Number.POSITIVE_INFINITY, suffix: '', isNumeric: false };
  }
  const match = trimmed.match(/^(\d+)(.*)$/);
  if (!match) {
    return { number: Number.POSITIVE_INFINITY, suffix: trimmed.toLowerCase(), isNumeric: false };
  }
  return {
    number: Number(match[1]),
    suffix: match[2]?.toLowerCase() || '',
    isNumeric: true,
  };
};

const compareTaskCodes = (a, b) => {
  const valueA = sanitizeCodePart(a?.code) || sanitizeCodePart(a?.name);
  const valueB = sanitizeCodePart(b?.code) || sanitizeCodePart(b?.name);

  if (!valueA && !valueB) {
    return 0;
  }

  if (!valueA) {
    return 1;
  }

  if (!valueB) {
    return -1;
  }

  const partsA = parseTaskCodeSortParts(valueA);
  const partsB = parseTaskCodeSortParts(valueB);

  if (partsA.isNumeric !== partsB.isNumeric) {
    return partsA.isNumeric ? -1 : 1;
  }

  if (partsA.number !== partsB.number) {
    return partsA.number - partsB.number;
  }

  if (partsA.suffix !== partsB.suffix) {
    return partsA.suffix.localeCompare(partsB.suffix);
  }

  return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
};

const expandHierarchySelection = (value, tree) => {
  const ids = Array.isArray(value)
    ? value
    : value !== undefined && value !== null && value !== ''
      ? [value]
      : [];

  if (!ids.length || !tree?.nodesById) {
    return Array.isArray(value) ? [...ids] : ids;
  }

  const { nodesById } = tree;
  const result = new Set();
  const stack = [];

  const pushNode = (id) => {
    if (id === undefined || id === null || id === '') {
      return;
    }
    const node = nodesById[id];
    if (!node) {
      result.add(id);
      return;
    }
    stack.push(node);
  };

  ids.forEach((id) => pushNode(id));

  while (stack.length) {
    const current = stack.pop();
    if (!current || result.has(current.id)) {
      continue;
    }
    result.add(current.id);
    current.children.forEach((child) => stack.push(child));
  }

  return Array.from(result);
};

const resolveHierarchyValue = (value, isMulti, tree) => {
  if (isMulti) {
    return expandHierarchySelection(value, tree);
  }

  const expanded = expandHierarchySelection(value, tree);
  if (!expanded.length) {
    return null;
  }
  if (expanded.length === 1) {
    return expanded[0];
  }
  return expanded;
};

const mergePaginatedOptionLists = (existing = [], incoming = []) => {
  const safeExisting = Array.isArray(existing) ? existing : [];
  const safeIncoming = Array.isArray(incoming) ? incoming : [];

  if (!safeExisting.length) {
    return [...safeIncoming];
  }

  if (!safeIncoming.length) {
    return [...safeExisting];
  }

  const result = [];
  const seen = new Map();

  const pushOption = (option) => {
    if (!option) return;
    const hasValue = option.value !== undefined && option.value !== null;
    const key = hasValue ? `value:${option.value}` : null;

    if (key) {
      if (seen.has(key)) {
        result[seen.get(key)] = option;
        return;
      }
      seen.set(key, result.length);
    }

    result.push(option);
  };

  safeExisting.forEach(pushOption);
  safeIncoming.forEach(pushOption);

  return result;
};

const normalizePaginatedResult = (result, page) => {
  if (Array.isArray(result)) {
    return { options: result, hasMore: false };
  }

  if (!result || typeof result !== 'object') {
    return { options: [], hasMore: false };
  }

  const options = Array.isArray(result.options)
    ? result.options
    : Array.isArray(result.data)
      ? result.data
      : Array.isArray(result.results)
        ? result.results
        : [];

  const pageSize =
    typeof result.pageSize === 'number'
      ? result.pageSize
      : typeof result.page_size === 'number'
        ? result.page_size
        : typeof result.limit === 'number'
          ? result.limit
          : undefined;

  const totalCount =
    typeof result.totalCount === 'number'
      ? result.totalCount
      : typeof result.total === 'number'
        ? result.total
        : typeof result.count === 'number'
          ? result.count
          : undefined;

  let hasMore;
  if (typeof result.hasMore === 'boolean') {
    hasMore = result.hasMore;
  } else if (typeof result.has_more === 'boolean') {
    hasMore = result.has_more;
  } else if (typeof result.nextPage === 'number') {
    hasMore = result.nextPage > page;
  } else if (typeof result.next_page === 'number') {
    hasMore = result.next_page > page;
  } else if (typeof totalCount === 'number' && typeof pageSize === 'number') {
    hasMore = page * pageSize < totalCount;
  } else if (typeof result.remaining === 'number') {
    hasMore = result.remaining > 0;
  } else if (typeof pageSize === 'number') {
    hasMore = Array.isArray(options) && options.length === pageSize;
  } else {
    hasMore = false;
  }

  return {
    options: Array.isArray(options) ? options : [],
    hasMore,
  };
};

export const useTableFilters = (filterConfig, initialFilters = {}, callbacks = {}) => {
  const [filterOptions, setFilterOptions] = useState({});
  const [tempFilters, setTempFilters] = useState(initialFilters);
  const tempFiltersRef = useRef(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [showDropdown, setShowDropdown] = useState(false);
  const [paginatedOptions, setPaginatedOptions] = useState({});
  const [paginatedLoadingMap, setPaginatedLoadingMap] = useState({});
  const [hierarchyTrees, setHierarchyTrees] = useState({});
  const [tableKey, setTableKey] = useState(0);
  const [lookupReloadKey, setLookupReloadKey] = useState(0);
  const dropdownRef = useRef(null);
  const moreFiltersButtonRef = useRef(null);
  const paginatedStateRef = useRef({});
  const menuBarFilters = useMemo(
    () => filterConfig.filter(({ menuBarFilter }) => !!menuBarFilter),
    [filterConfig],
  );
  const dropdownFilters = useMemo(
    () => filterConfig.filter(({ menuBarFilter }) => !menuBarFilter),
    [filterConfig],
  );
  const menuBarFilterFields = useMemo(
    () => new Set(menuBarFilters.map(({ field }) => field)),
    [menuBarFilters],
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const clickedInsideReactSelectPortal =
        typeof Element !== 'undefined' &&
        target instanceof Element &&
        Boolean(target.closest('.react-select__menu-portal, .react-select__menu'));

      if (clickedInsideReactSelectPortal) {
        return;
      }

      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    const unsubscribe = subscribeToLookupCache((api) => {
      const relevantFields = filterConfig.filter(({ api: cfgApi, type }) => {
        if (type !== 'dropDown' || !cfgApi) return false;
        return api === null || cfgApi === api;
      });

      if (!relevantFields.length) {
        return;
      }

      const fields = relevantFields.map(({ field }) => field);

      setFilterOptions((prev) => {
        if (!prev) return prev;
        let changed = false;
        const next = { ...prev };
        fields.forEach((field) => {
          if (field in next) {
            delete next[field];
            changed = true;
          }
        });
        return changed ? next : prev;
      });

      setHierarchyTrees((prev) => {
        if (!prev) return prev;
        let changed = false;
        const next = { ...prev };
        fields.forEach((field) => {
          if (field in next) {
            delete next[field];
            changed = true;
          }
        });
        return changed ? next : prev;
      });

      setLookupReloadKey((prev) => prev + 1);
    });

    return unsubscribe;
  }, [filterConfig]);

  const fetchPaginatedField = useCallback(
    async (fieldKey, loader, { search, page, optionSortField }) => {
      if (typeof loader !== 'function') {
        paginatedStateRef.current[fieldKey] = {
          term: search,
          page,
          hasMore: false,
        };
        if (page === 1) {
          setPaginatedOptions((prev) => ({ ...prev, [fieldKey]: [] }));
        }
        return [];
      }

      setPaginatedLoadingMap((prev) => ({ ...prev, [fieldKey]: true }));

      try {
        const result = await loader(search, { page, search });
        const { options: fetchedOptions, hasMore } = normalizePaginatedResult(result, page);
        const sortedOptions = sortLookupOptions(fetchedOptions, {
          optionSortField,
          getLabel: (option) => option?.fullLabel || option?.label || '',
        });

        setPaginatedOptions((prev) => {
          const prevList = Array.isArray(prev[fieldKey]) ? prev[fieldKey] : [];
          const nextList = page === 1
            ? [...sortedOptions]
            : sortLookupOptions(mergePaginatedOptionLists(prevList, sortedOptions), {
              optionSortField,
              getLabel: (option) => option?.fullLabel || option?.label || '',
            });
          return {
            ...prev,
            [fieldKey]: nextList,
          };
        });

        paginatedStateRef.current[fieldKey] = {
          term: search,
          page,
          hasMore: Boolean(hasMore),
        };

        return Array.isArray(sortedOptions) ? sortedOptions : [];
      } catch (err) {
        console.error('Error fetching paginated options for', fieldKey, err);
        paginatedStateRef.current[fieldKey] = {
          term: search,
          page,
          hasMore: false,
        };
        if (page === 1) {
          setPaginatedOptions((prev) => ({ ...prev, [fieldKey]: [] }));
        }
        return [];
      } finally {
        setPaginatedLoadingMap((prev) => ({ ...prev, [fieldKey]: false }));
      }
    },
    [],
  );

  // Fetch options for select filters only
  useEffect(() => {
    filterConfig.forEach(({ field, api, type, displayField, options, optionSortField }) => {
      if (type !== 'dropDown') return;

      if (Array.isArray(options)) {
        setFilterOptions(prev => ({ ...prev, [field]: options }));
        return;
      }

      if (!api) return; // only fetch for dropDown type with api

      const cached = getCache(api);
      const applyOptions = (arr) => {
        const sortedItems = sortLookupOptions(arr, {
          optionSortField,
          compareFn: api === 'task-codes' ? compareTaskCodes : undefined,
          getLabel: (item) => getOptionLabel(item, api, displayField),
        });
        const baseOptions = sortedItems.map(item => {
          const codeNameLabel = api === 'task-codes' ? buildCodeNameLabel(item) : '';
          const bootstrapName =
            item?.colours?.bootstrapName ||
            item?.colour?.bootstrapName ||
            item?.bootstrapName ||
            item?.colour ||
            '';
          const opt = {
            value: item.id,
            label:
              codeNameLabel ||
              (typeof displayField === 'function'
                ? displayField(item)
                : getNestedValue(item, displayField) || `ID ${item.id}`),
          };
          if (item?.colours) {
            opt.colours = item.colours;
          }
          if (bootstrapName) {
            opt.colour = bootstrapName;
            opt.bootstrapName = bootstrapName;
          }
          if (api === HIERARCHY_STRUCTURE_API) {
            opt.parentId = item.parentId;
            const parent = arr.find(p => p.id === item.parentId);
            opt.parentName = parent ? parent.name : '';
          }
          return opt;
        });

        if (api === HIERARCHY_STRUCTURE_API) {
          const { options: enhanced, tree } = enhanceHierarchyOptions(baseOptions);
          setHierarchyTrees(prev => ({ ...prev, [field]: tree }));
          setFilterOptions(prev => ({ ...prev, [field]: enhanced }));
          return;
        }

        setFilterOptions(prev => ({ ...prev, [field]: baseOptions }));
      };

      if (cached) {
        const arr = Array.isArray(cached)
          ? cached
          : Array.isArray(cached?.data)
            ? cached.data
            : [];
        applyOptions(arr);
      } else {
        const fetchApiName = resolveHierarchyApiName(api);
        getOrFetch(api, () => fetchData(getAll, { apiName: fetchApiName }))
          .then((data) => {
            const arr = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
            applyOptions(arr);
          })
          .catch(err => {
            console.error('Error fetching options for', api, err);
          });
      }
    });
  }, [filterConfig, lookupReloadKey]);

  // Fetch first page for paginated dropdowns
  useEffect(() => {
    filterConfig.forEach(({ field, type, loadOptions, optionSortField }) => {
      if (type !== 'paginatedDropdown' || typeof loadOptions !== 'function') return;
      (async () => {
        await fetchPaginatedField(field, loadOptions, { search: '', page: 1, optionSortField });
      })();
    });
  }, [filterConfig, fetchPaginatedField]);

  const setTableKeyLocal = () => {
    setTableKey(prevKey => prevKey + 1);
  };

  // Apply filters sets appliedFilters to tempFilters
  const applyFilters = (filtersToApply = tempFiltersRef.current) => {
    const mergedFilters = {
      ...appliedFilters,
      ...tempFiltersRef.current,
      ...(filtersToApply || {}),
    };

    const applied = {};
    const currentFilters = mergedFilters;
    filterConfig.forEach(({ field, type, isMulti = true }) => {
      if (type === 'paginatedDropdown' || type === 'dropDown') {
        applied[field] = isMulti ? (currentFilters[field] || []) : (currentFilters[field] ?? null);
      } else {
        applied[field] = currentFilters[field];
      }
    });
    setAppliedFilters(applied);
    setShowDropdown(false);
    setTableKey(prevKey => prevKey + 1);
    if (typeof callbacks.onApply === 'function') {
      callbacks.onApply(applied);
    }
  };

  // Helper to update tempFilters, handling both select and date types
  const setTempFilter = (field, value) => {
    const next = { ...tempFiltersRef.current, [field]: value };
    tempFiltersRef.current = next;
    setTempFilters(next);

    if (menuBarFilterFields.has(field)) {
      applyFilters(next);
    }
  };

  const setTempFiltersBatch = (updates = {}, { apply = true } = {}) => {
    const next = { ...tempFiltersRef.current, ...updates };
    tempFiltersRef.current = next;
    setTempFilters(next);

    if (apply) {
      applyFilters(next);
    }
  };

  // Reset filters to initial values (if provided) or clear them
  const resetFilters = () => {
    const cleared = Object.fromEntries(filterConfig.map(f => {
      const initial = initialFilters[f.field];
      if (initial !== undefined) {
        if (Array.isArray(initial)) {
          return [f.field, initial.map(val => (typeof val === 'object' && val !== null ? { ...val } : val))];
        }
        if (typeof initial === 'object' && initial !== null) {
          return [f.field, { ...initial }];
        }
        return [f.field, initial];
      }
      if (f.type === 'switch') return [f.field, false];
      if (f.type === 'date' || f.type === 'datetime') return [f.field, ''];
      if (f.type === 'text') return [f.field, ''];
      if (f.type === 'range') return [f.field, {}];
      if (f.type === 'paginatedDropdown' || f.type === 'dropDown') {
        return [f.field, f.isMulti === false ? null : []];
      }
      return [f.field, []];
    }));
    tempFiltersRef.current = cleared;
    setTempFilters(cleared);
    setAppliedFilters(cleared);
    setTableKey(prevKey => prevKey + 1);
    if (typeof callbacks.onReset === 'function') {
      callbacks.onReset(cleared);
    }
  };

  const clearFilters = () => {
    resetFilters();
    if (typeof callbacks.onClearFilters === 'function') {
      callbacks.onClearFilters();
    }
  };

  const appliedFilterParams = useMemo(() => {
    const resolved = {};
    filterConfig.forEach(({ field, type, isMulti = true, api }) => {
      const rawValue = appliedFilters[field];
      if (type === 'range') {
        const rangeVal = rawValue || {};
        if (rangeVal.min !== undefined && rangeVal.min !== null && rangeVal.min !== '') {
          resolved[`${field}Min`] = rangeVal.min;
        }
        if (rangeVal.max !== undefined && rangeVal.max !== null && rangeVal.max !== '') {
          resolved[`${field}Max`] = rangeVal.max;
        }
      } else if (type === 'dropDown' || type === 'paginatedDropdown') {
        if (api === HIERARCHY_STRUCTURE_API) {
       // HierarchySelect already cascades selection to children at toggle time,
          // so rawValue already contains every ID that should be queried. Calling
          // expandHierarchySelection here would re-add children that were explicitly
          // deselected by the user. Pass the raw array straight through.
          resolved[field] = isMulti
            ? (Array.isArray(rawValue) ? rawValue : rawValue != null ? [rawValue] : [])
            : (rawValue ?? null);
        } else if (type === 'paginatedDropdown') {
          if (isMulti) {
            resolved[field] = Array.isArray(rawValue)
              ? rawValue.map((option) => option?.value ?? option).filter((value) => value !== undefined && value !== null)
              : [];
          } else {
            resolved[field] = rawValue?.value ?? rawValue ?? null;
          }
        } else {
          resolved[field] = rawValue;
        }
      } else {
        resolved[field] = rawValue;
      }
    });
    return resolved;
  }, [appliedFilters, filterConfig, hierarchyTrees]);

  const renderFilterControl = (config, inline = false, wrapperClassName) => {
    const { field, label, type, isMulti = true } = config;
    const dropDownWidth = config.field === "departmentId" ||  config.field === "departmentIds" ? "340px" : "240px";
    const isHierarchy = config.api === HIERARCHY_STRUCTURE_API;
    const commonClassName = wrapperClassName !== undefined ? wrapperClassName : (inline ? 'd-flex flex-column' : 'col-md-6');
    const wrapperStyle = inline ? { minWidth: (type === 'switch' ? '85px' : dropDownWidth), maxWidth: '400px' } : undefined;
    const controlHeight = 42;
    const sharedSelectStyles = {
      control: (base, state) => ({
        ...base,
        minHeight: `${controlHeight}px`,
        height: `${controlHeight}px`,
        borderRadius: '4px',
        boxShadow: 'none',
        borderColor: state.isFocused ? base.borderColor : base.borderColor,
      }),
      valueContainer: (base) => ({
        ...base,
        height: `${controlHeight - 2}px`,
        paddingTop: 0,
        paddingBottom: 0,
      }),
      input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
      }),
      indicatorsContainer: (base) => ({
        ...base,
        height: `${controlHeight - 2}px`,
      }),
      dropdownIndicator: (base) => ({
        ...base,
        paddingTop: 0,
        paddingBottom: 0,
      }),
      clearIndicator: (base) => ({
        ...base,
        paddingTop: 0,
        paddingBottom: 0,
      }),
      singleValue: (base) => ({
        ...base,
        lineHeight: `${controlHeight - 2}px`,
      }),
      menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    };

    const ValueContainer = (props) => {
      const { selectProps, getValue, hasValue, children } = props;

      if (!selectProps.isMulti || !hasValue) {
        return <selectComponents.ValueContainer {...props}>{children}</selectComponents.ValueContainer>;
      }

      const values = getValue();
      if (!Array.isArray(values) || !values.length) {
        return <selectComponents.ValueContainer {...props}>{children}</selectComponents.ValueContainer>;
      }

      const firstLabel = values[0]?.label ?? values[0]?.fullLabel ?? '';
      const displayLabel = values.length === 1
        ? firstLabel
        : (isHierarchy ? `${values.length} Departments` : `${values.length} Selected`);
      const input = Array.isArray(children) ? children[children.length - 1] : children;

      return (
        <selectComponents.ValueContainer {...props}>
          <div className="react-select__single-value">{displayLabel}</div>
          {input}
        </selectComponents.ValueContainer>
      );
    };

    return (
      <div className={commonClassName} style={wrapperStyle} key={field}>
        <label className="form-label">{label}{config.required && <span className="text-danger">*</span>}</label>
        {type === 'dropDown' && (
          isHierarchy ? (
            <HierarchySelect
              isMulti={isMulti}
              options={filterOptions[field] || []}
              tree={hierarchyTrees[field]}
              value={isMulti ? (tempFilters[field] || []) : tempFilters[field] ?? null}
              onChange={selected => setTempFilter(field, selected)}
              components={{ ValueContainer }}
            />
          ) : (
            <Select
              isMulti={isMulti}
              classNamePrefix="react-select"
              value={isMulti
                ? (filterOptions[field] || []).filter(opt => tempFilters[field]?.includes(opt.value))
                : (filterOptions[field] || []).find(opt => opt.value === tempFilters[field]) || null}
              onChange={selected => setTempFilter(field,
                isMulti ? (selected ? selected.map(opt => opt.value) : []) : (selected ? selected.value : null))}
              options={filterOptions[field] || []}
              formatOptionLabel={config.formatOptionLabel}
              components={{ ValueContainer }}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={sharedSelectStyles}
              {...(config.maxMenuHeight !== undefined ? { maxMenuHeight: config.maxMenuHeight } : {})}
              />
          )
        )}
        {type === 'paginatedDropdown' && (
          <AsyncSelect
            classNamePrefix="react-select"
            loadOptions={async (inputValue) => {
              const raw = typeof inputValue === 'string' ? inputValue : '';
              return fetchPaginatedField(field, config.loadOptions, {
                search: raw,
                page: 1,
                optionSortField: config.optionSortField,
              });
            }}
            cacheOptions
            defaultOptions={paginatedOptions[field] || []}
            value={isMulti ? tempFilters[field] || [] : tempFilters[field] || null}
            onChange={(opts) => {
              const nextValue = isMulti ? (opts || []) : (opts || null);
              setTempFilter(field, nextValue);
              if (!opts) {
                fetchPaginatedField(field, config.loadOptions, {
                  search: '',
                  page: 1,
                  optionSortField: config.optionSortField,
                });
              }
            }}
            isMulti={isMulti}
            closeMenuOnSelect={!isMulti}
            placeholder="Start typing to search ..."
            formatOptionLabel={isHierarchy ? formatDepartmentOption : config.formatOptionLabel}
            components={{ ValueContainer }}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            styles={sharedSelectStyles}
            onMenuScrollToBottom={() => {
              const state = paginatedStateRef.current[field] || {};
              if (paginatedLoadingMap[field] || !state.hasMore) return;
              const nextPage = (state.page || 1) + 1;
              const searchTerm = typeof state.term === 'string' ? state.term : '';
              fetchPaginatedField(field, config.loadOptions, {
                search: searchTerm,
                page: nextPage,
                optionSortField: config.optionSortField,
              });
            }}
            isLoading={Boolean(paginatedLoadingMap[field])}
          />
        )}
        {(type === 'date') && (
          <input
            type="date"
            className="form-control"
            value={tempFilters[field] || ''}
            onChange={e => setTempFilter(field, e.target.value)}
          />
        )}
        {type === 'datetime' && (
          <div className="mb-3">
            <label className="form-label">{label}</label>
            <input
              type="datetime-local"
              className="form-control"
              value={tempFilters[field] || ''}
              onChange={e => setTempFilter(field, e.target.value)}
            />
          </div>
        )}
        {type === 'switch' && (
          <div className="form-switch switch-primary d-flex align-items-center gap-3 mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id={`switch-${field}`}
              checked={!!tempFilters[field]}
              onChange={e => setTempFilter(field, e.target.checked)}
            />
            <label className="form-check-label line-height-1 fw-medium text-secondary-light">
              {tempFilters[field] ? 'Yes' : 'No'}
            </label>
          </div>
        )}
        {type === 'text' && (
          <input
            type="text"
            className="form-control"
            value={tempFilters[field] || ''}
            onChange={e => setTempFilter(field, e.target.value)}
            placeholder={config.placeholder || ''}
          />
        )}
        {type === 'range' && (
          <div className="d-flex gap-2">
            <input
              type="number"
              className="form-control"
              placeholder="Min"
              value={(tempFilters[field] || {}).min ?? ''}
              onChange={e => {
                const val = e.target.value === '' ? undefined : Number(e.target.value);
                setTempFilter(field, { ...(tempFilters[field] || {}), min: val });
              }}
            />
            <input
              type="number"
              className="form-control"
              placeholder="Max"
              value={(tempFilters[field] || {}).max ?? ''}
              onChange={e => {
                const val = e.target.value === '' ? undefined : Number(e.target.value);
                setTempFilter(field, { ...(tempFilters[field] || {}), max: val });
              }}
            />
          </div>
        )}
      </div>
    );
  };

  // Render filter inputs based on type
  const renderFilterDropdown = () => {
    const button = moreFiltersButtonRef.current;
    const buttonRect =
      button && typeof button.getBoundingClientRect === 'function'
        ? button.getBoundingClientRect()
        : null;
    const width = Math.min(760, typeof window !== 'undefined' ? window.innerWidth - 32 : 760);
    const left = buttonRect
      ? Math.max(16, Math.min(buttonRect.left, (typeof window !== 'undefined' ? window.innerWidth : width) - width - 16))
      : 16;
    const dropdownStyle = {
      width: `${width}px`,
      maxWidth: 'calc(100vw - 32px)',
      display: 'block',
      position: 'fixed',
      top: buttonRect ? `${buttonRect.bottom + 8}px` : '70px',
      left: `${left}px`,
      zIndex: 99999,
    };

    const dropdown = (
      <div
        ref={dropdownRef}
        className="dropdown-menu show p-3"
        style={dropdownStyle}
      >
        <label className="form-label text-lg mb-0">Select Filters</label>
        <div className="row">
          {dropdownFilters.map((config) => (
            <React.Fragment key={config.field}>
              {renderFilterControl(config)}
              {typeof config.renderAfter === 'function' && config.renderAfter(tempFilters)}
            </React.Fragment>
          ))}
        </div>
        <div className="text-end">
          <button
            id="rs-filters-btn"
            className="btn btn-outline-secondary mt-2 me-12"
            onClick={resetFilters}
          >
            Reset Filters
          </button>
          <button
            id="apply-filters-btn"
            className="btn btn-primary mt-2"
            onClick={applyFilters}
          >
            <i className="bi bi-check-circle-fill me-2" /> Apply Filters
          </button>
        </div>
      </div>
    );

    if (typeof document === 'undefined') {
      return dropdown;
    }

    return createPortal(dropdown, document.body);
  };

  const renderMenuBarFilters = () => {
    if (!menuBarFilters.length) return null;
    return (
      <div className="d-flex align-items-center flex-wrap gap-3 me-4">
        {menuBarFilters.map((config) => renderFilterControl(config, true))}
      </div>
    );
  };

  // Render badges for selected filters
  const renderFilterBadges = () =>
    filterConfig.map((config) => {
      const { field, label, type } = config;
      const selected = appliedFilters[field];
      if (!selected) return null;

      if (type === 'date') {
        if (typeof selected === 'string' && selected.trim() !== '') {
          return (
            <div key={field} className="badge rounded-pill text-primary border border-secondary mx-6">
              <strong>{label}:</strong> {selected}
            </div>
          );
        }
        if (typeof selected === 'object' && selected !== null) {
          if (!selected.start && !selected.end) return null;
          const tz = getUserTimeZone();
          const startLabel = selected.start ? formatLocal(selected.start, tz, 'yyyy-MM-dd') : '';
          const endLabel = selected.end ? formatLocal(selected.end, tz, 'yyyy-MM-dd') : '';
          return (
            <div key={field} className="badge rounded-pill text-primary border border-secondary mx-6">
              <strong>{label}:</strong> {startLabel} - {endLabel}
            </div>
          );
        }
        return null;
      }
      if (type === 'text') {
        if (typeof selected === 'string' && selected.trim() !== '') {
          return (
            <div key={field} className="badge rounded-pill text-primary border border-secondary mx-6">
              <strong>{label}:</strong> {selected}
            </div>
          );
        }
        return null;
      }
      if (type === 'range') {
        if (typeof selected === 'object' && selected !== null) {
          const parts = [];
          if (selected.min !== undefined && selected.min !== null && selected.min !== '') parts.push(`Min: ${selected.min}`);
          if (selected.max !== undefined && selected.max !== null && selected.max !== '') parts.push(`Max: ${selected.max}`);
          if (parts.length === 0) return null;
          return (
            <div key={field} className="badge rounded-pill text-primary border border-secondary mx-6">
              <strong>{label}:</strong> {parts.join(', ')}
            </div>
          );
        }
        return null;
      }
      if (type === 'datetime') {
        const tz = getUserTimeZone();
        const labelVal = selected ? formatLocal(selected, tz) : '';
        if (!labelVal) return null;
        return (
          <div key={field} className="badge rounded-pill text-primary border border-secondary mx-6">
            <strong>{label}:</strong> {labelVal}
          </div>
        );
      }

      if (type === 'paginatedDropdown' && Array.isArray(selected) && selected.length > 0) {
        const labels = selected
          .map(option => option?.label ?? option?.value ?? '')
          .filter(optionLabel => optionLabel !== null && optionLabel !== undefined);
        const displayLabel = labels.length ? labels.join(', ') : `${selected.length} selected`;
        return (
          <div key={field} className="badge rounded-pill text-primary border border-secondary mx-6">
            <strong>{label}:</strong> {displayLabel}
          </div>
        );
      }

      if (type === 'paginatedDropdown' && selected && !Array.isArray(selected)) {
        const displayLabel = selected?.label ?? selected?.value ?? selected;
        return (
          <div key={field} className="badge rounded-pill text-primary border border-secondary mx-6">
            <strong>{label}:</strong> {displayLabel}
          </div>
        );
      }

      // For dropDown multi-selects
      if (Array.isArray(selected) && selected.length > 0) {
        const options = filterOptions[field] || [];
        const labels = selected.map((selectedValue) => {
          const option = options.find(opt => opt.value === selectedValue);
          return option ? (option.fullLabel || option.label) : selectedValue;
        }).filter(optionLabel => optionLabel !== null && optionLabel !== undefined);
        const isHierarchyMultiSelect = config.api === HIERARCHY_STRUCTURE_API && selected.length > 1;
        const displayLabel = isHierarchyMultiSelect
          ? `${selected.length} Selected`
          : (labels.length ? labels.join(', ') : `${selected.length} selected`);
        return (
          <div key={field} className="badge rounded-pill text-primary border border-secondary mx-6">
            <strong>{label}:</strong> {displayLabel}
          </div>
        );
      }

      if (type === 'dropDown' && !Array.isArray(selected)) {
        const options = filterOptions[field] || [];
        const option = options.find(opt => opt.value === selected);
        const displayLabel = option ? option.fullLabel || option.label : selected;
        return (
          <div key={field} className="badge rounded-pill text-primary border border-secondary mx-6">
            <strong>{label}:</strong> {displayLabel}
          </div>
        );
      }

      if (type === 'switch' && selected) {
        return (
          <div key={field} className="badge rounded-pill text-primary border border-secondary mx-6">
            <strong>{label}</strong>
          </div>
        );
      }

      return null;
    });

  const renderFilterControls = () => (
    <div className="dropdown">
      <div className="d-flex align-items-start">
        {renderMenuBarFilters()}
        <button
          id="apply-filters-btn"
          className="d-none w-32-px h-32-px bg-secondary-focus text-secondary-main rounded-circle d-inline-flex align-items-center justify-content-center ms-3"
          onClick={applyFilters}
        >
          <Icon icon="mage:check" className="icon text-xl line-height-1" />
        </button>
        <button
          className="btn btn-link text-primary p-0 ms-16"
          style={{ marginTop: "34px" }}
          type="button"
          ref={moreFiltersButtonRef}
          onClick={() => setShowDropdown(prev => !prev)}
        >
          More Filters
        </button>
        <button
          id="reset-filters-btn"
          style={{ marginTop: "34px" }}
          className="btn btn-link text-primary p-0 ms-16"
          onClick={clearFilters}
        >
          Clear Filters
        </button>
      </div>
      <div className="mt-8" style={{ minHeight: '30px' }}>
        <Icon icon="mdi:filter-outline" className="icon text-xl line-height-1 text-secondary" />
        {renderFilterBadges()}
      </div>
      {showDropdown && renderFilterDropdown()}
    </div>
  );

  return {
    filterOptions,
    tempFilters,
    appliedFilters,
    appliedFilterParams,
    setTempFilter,
    applyFilters,
    resetFilters,
    showDropdown,
    setShowDropdown,
    renderFilterControl,
    renderFilterDropdown,
    renderFilterControls,
    renderMenuBarFilters,
    renderFilterBadges,
    tableKey,
    setTableKeyLocal,
    setTempFiltersBatch
  };
};
